import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = '/build/assets/pdf.worker.min.mjs';

/**
 * Extract text from PDF file
 * @param {File} pdfFile - The PDF file to extract text from
 * @returns {Promise<string>} The extracted text
 */
async function extractPdfText(pdfFile) {
    try {
        const arrayBuffer = await readFileAsArrayBuffer(pdfFile);
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        const totalPages = pdf.numPages;

        let fullText = '';

        for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
            const page = await pdf.getPage(pageNum);
            const textContent = await page.getTextContent();

            // Build text with better spacing handling
            let lastY = null;
            let lastRight = null;
            let pageText = '';

            for (const item of textContent.items) {
                const x = Array.isArray(item.transform) ? item.transform[4] : null;

                // Check if we're on a new line (Y position changed significantly)
                if (lastY !== null && Math.abs(item.transform[5] - lastY) > 5) {
                    pageText += '\n';
                    lastRight = null;
                }

                // Add space before text if needed (unless it's at the start of a line)
                if (pageText.length > 0 && !pageText.endsWith('\n') && !pageText.endsWith(' ')) {
                    // PDF.js hands back whatever chunks the file's own text operators
                    // define, and that boundary can fall inside a number: a quantity
                    // of 582.350 arrives as "582.3" then "50". A space there splits
                    // one figure into two, the row is read one column across, and the
                    // quantity ends up short by its last digits. Chunks that touch on
                    // the page — no gap between them — and continue a number belong to
                    // the same number, so they are put back together. A real column
                    // gap is several points wide and still gets its space.
                    const continuesANumber = /[\d.,]$/.test(pageText) && /^\d/.test(item.str);
                    const touching = x !== null && lastRight !== null && (x - lastRight) < 1;

                    if (!continuesANumber || !touching) {
                        pageText += ' ';
                    }
                }

                pageText += item.str;
                lastY = item.transform[5];
                lastRight = x !== null ? x + (item.width || 0) : null;
            }

            fullText += pageText + '\n';
        }

        // Debug: log first 1000 chars
        console.log('📄 Extracted PDF text (first 1000 chars):', fullText.substring(0, 1000));

        return fullText;
    } catch (error) {
        console.error('Error extracting PDF text:', error);
        throw error;
    }
}

/**
 * Check if PDF is a Bulgarian Gas Station invoice
 * Pattern: Invoice number + date + "Данъчна" + EIK + Company name + totals
 * @param {string} text - The PDF text content
 * @returns {boolean}
 */
export function isBulgarianGasStationInvoice(text) {
    // Look for Bulgarian gas station patterns
    const patterns = [
        /Данъчна/i, // Bulgarian tax invoice marker
        /ДИЗЕЛОВО ГОРИВО|Дизелово гориво/i, // Diesel fuel
        /Бар код.*Вътр\.код/i, // Barcode and internal code headers
        /\d{10}\s+[\d.]+\s*г\.\s*Данъчна\s+\d+/i, // Invoice pattern
    ];

    // Must match at least 2 patterns to be considered a gas station invoice
    const matchCount = patterns.filter(pattern => pattern.test(text)).length;
    return matchCount >= 2;
}

/**
 * Extract structured data from Bulgarian Gas Station invoice
 * Returns: { companies: [{ company_name, eik, invoices: [{invoice_number, date, items: [...]}] }] }
 * @param {string} text - The PDF text content
 * @returns {Object} Structured data
 */
export function extractBulgarianGasStationData(text) {
    const companies = {};
    const lines = text.split('\n');

    let currentCompany = null;
    let currentInvoiceNumber = null;
    let isReadingItems = false;

    console.log(`🔍 Processing ${lines.length} lines from PDF...`);

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const trimmedLine = line.trim();

        if (!trimmedLine) {
            continue;
        }

        // Debug logging for important patterns
        if (trimmedLine.includes('Име на') || trimmedLine.includes('Бар код') || /Отпечатано\s*на:/i.test(trimmedLine)) {
            console.log(`🔍 [Line ${i}] Key pattern detected: "${trimmedLine.substring(0, 100)}"`);
            console.log(`   State: isReadingItems=${isReadingItems}, currentInvoice=${currentInvoiceNumber}, currentCompany=${currentCompany}`);
        }

        // Log lines that look like items
        if (/^(\d+)\s+(\d+)\s+/.test(trimmedLine)) {
            console.log(`🔍 [Line ${i}] Potential item: "${trimmedLine.substring(0, 100)}"`);
            console.log(`   State: isReadingItems=${isReadingItems}, currentInvoice=${currentInvoiceNumber}`);
        }

        // Check if this is an invoice header line
        // Pattern: Invoice number + date + "Данъчна" + EIK + Company name + totals
        // Handle both space and tab separators
        const invoiceMatch = trimmedLine.match(/^(\d{10})\s+([\d.]+\s*г\.?)\s*Данъчна\s+(\d+)\s+(.+?)\s+([\d,]+\.?\d*)\s+([\d,]+\.?\d*)\s+([\d,]+\.?\d*)\s*$/);

        if (invoiceMatch) {
            const [, invoiceNumber, date, eik, companyName, totalWithoutVAT, vat, totalWithVAT] = invoiceMatch;
            const cleanCompanyName = companyName.trim();

            console.log(`✅ [Line ${i}] Found invoice: ${invoiceNumber} for ${cleanCompanyName}`);
            console.log(`   Previous state: isReadingItems=${isReadingItems}, currentInvoice=${currentInvoiceNumber}`);
            console.log(`   Invoice details: date=${date}, total=${totalWithVAT}`);

            // Initialize company if not exists
            if (!companies[cleanCompanyName]) {
                companies[cleanCompanyName] = {
                    company_name: cleanCompanyName,
                    eik: eik,
                    invoices: {}
                };
            }

            // Create new invoice
            currentCompany = cleanCompanyName;
            currentInvoiceNumber = invoiceNumber;
            companies[cleanCompanyName].invoices[invoiceNumber] = {
                invoice_number: invoiceNumber,
                date: date,
                total_without_vat: parseFloat(totalWithoutVAT.replace(',', '.')),
                vat: parseFloat(vat.replace(',', '.')),
                total_with_vat: parseFloat(totalWithVAT.replace(',', '.')),
                items: []
            };

            isReadingItems = false;
            console.log(`   New state: currentInvoice=${currentInvoiceNumber}, isReadingItems=${isReadingItems}`);
            continue;
        }

        // Check for summary table header "Име на фирма" - we should SKIP this table entirely
        // This is NOT the items table, it's a summary table that appears between invoices
        if (/Име\s*на\s*фирма/i.test(trimmedLine) && /ДДС/i.test(trimmedLine)) {
            console.log(`⚠️ [Line ${i}] Summary table header detected ("Име на фирма"), will skip this table`);
            console.log(`   Setting isReadingItems = false (was ${isReadingItems})`);
            isReadingItems = false;
            continue;
        }

        // Check if this is the header row for items table
        // This can appear multiple times if invoice spans multiple pages
        // Items table header: "Бар код | Вътр.код | Име на стока | Количество | Ед. цена, лв | Сума, лв"
        // Must have both "Бар код" AND "Вътр.код" (NOT "Име на фирма" which is the summary table)
        if (/Бар\s*код/i.test(trimmedLine) && /Вътр\.?\s*код/i.test(trimmedLine)) {
            if (currentInvoiceNumber) {
                console.log(`📋 [Line ${i}] Item table header detected, setting isReadingItems = true (was ${isReadingItems}) for invoice ${currentInvoiceNumber}`);
                isReadingItems = true;
            } else {
                console.log(`⚠️ [Line ${i}] Item table header detected but no current invoice context`);
            }
            continue;
        }

        // Stop reading items temporarily when we encounter page footer
        // But don't reset invoice context - it may continue on next page
        if (/Отпечатано\s*на:|Printed\s*on:/i.test(trimmedLine)) {
            if (isReadingItems) {
                console.log(`📄 [Line ${i}] Page break detected for invoice ${currentInvoiceNumber}, setting isReadingItems = false, will resume if table continues...`);
            }
            isReadingItems = false;
            continue;
        }

        // Resume reading items if we see a line that looks like it could be a continuation
        // Pattern: starts with barcode (any length) followed by internal code and text
        // But only if we have a current invoice context and we're not already reading items
        // This handles both 4-digit codes (0002 0002) and 13-digit barcodes (3266720012601 0248)
        if (!isReadingItems && currentInvoiceNumber && /^(\d+)\s+(\d+)\s+/.test(trimmedLine)) {
            console.log(`🔄 [Line ${i}] Detected item continuation pattern (no header), setting isReadingItems = true for invoice ${currentInvoiceNumber}`);
            isReadingItems = true;
            // Don't continue, let it process this line as an item
        }

        // Check if this is an item row
        if (isReadingItems && currentCompany && currentInvoiceNumber) {
            // Pattern for item row: barcode + internal_code + product_name + quantity + price + total
            // Barcode can be 4-13 digits (e.g., "0002" or "3266720012601")
            // Product name can have spaces (e.g., "BDC 1L" or "ДИЗЕЛОВО ГОРИВО")
            // Numbers can have commas or dots (e.g., "1.000" or "1,000")
            // Strategy: Match the last 3 number groups as quantity, unit_price, total
            const itemMatch = trimmedLine.match(/^(\d+)\s+(\d+)\s+(.+?)\s+([\d,]+\.?\d+)\s+([\d,]+\.?\d+)\s+([\d,]+\.?\d+)\s*$/);

            if (itemMatch) {
                const [, barcode, internalCode, productName, quantity, unitPrice, total] = itemMatch;

                companies[currentCompany].invoices[currentInvoiceNumber].items.push({
                    barcode: barcode,
                    internal_code: internalCode,
                    product_name: productName.trim(),
                    quantity: parseFloat(quantity.replace(',', '.')),
                    unit_price: parseFloat(unitPrice.replace(',', '.')),
                    total: parseFloat(total.replace(',', '.'))
                });

                console.log(`  ➕ Item added to invoice ${currentInvoiceNumber}: ${productName.trim()} (barcode: ${barcode}, qty: ${quantity}, total: ${total})`);
            } else if (trimmedLine.length > 10 && !trimmedLine.includes('Количество')) {
                // Debug: log lines that didn't match but should have (but skip column headers)
                console.log(`  ⚠️ Line didn't match item pattern: "${trimmedLine}"`);
                // Try to understand why it didn't match
                const parts = trimmedLine.split(/\s+/);
                console.log(`     Parts: [${parts.join(', ')}] (${parts.length} parts)`);
            }
        } else if (/^(\d+)\s+(\d+)\s+/.test(trimmedLine) && trimmedLine.length > 10) {
            // Log potential items that weren't captured
            console.log(`  ⚠️ Potential item line but isReadingItems=${isReadingItems}, currentInvoice=${currentInvoiceNumber}: "${trimmedLine}"`);
        }
    }

    // Convert to array format
    const companiesArray = Object.values(companies).map(company => ({
        ...company,
        invoices: Object.values(company.invoices)
    }));

    return {
        companies: companiesArray,
        summary: calculateSummary(companiesArray)
    };
}

/**
 * Calculate summary statistics
 * @param {Array} companies - Array of company data
 * @returns {Object} Summary statistics
 */
function calculateSummary(companies) {
    let totalCompanies = companies.length;
    let totalInvoices = 0;
    let totalItems = 0;
    let grandTotal = 0;

    for (const company of companies) {
        totalInvoices += company.invoices.length;
        for (const invoice of company.invoices) {
            totalItems += invoice.items.length;
            grandTotal += invoice.total_with_vat;
        }
    }

    return {
        total_companies: totalCompanies,
        total_invoices: totalInvoices,
        total_items: totalItems,
        grand_total: grandTotal
    };
}

/**
 * Process a PDF file and check if it's a Bulgarian Gas Station invoice
 * If yes, extract structured data; if no, return null
 * @param {File} pdfFile - The PDF file to process
 * @returns {Promise<Object|null>} Structured data or null
 */
export async function processBulgarianGasStationPdf(pdfFile) {
    try {
        console.log('🔍 Checking if PDF is Bulgarian Gas Station invoice...');

        // Extract text from PDF
        const text = await extractPdfText(pdfFile);

        // Check if it matches the pattern
        if (!isBulgarianGasStationInvoice(text)) {
            console.log('❌ Not a Bulgarian Gas Station invoice');
            return null;
        }

        console.log('✅ Detected Bulgarian Gas Station invoice!');

        // Extract structured data
        const data = extractBulgarianGasStationData(text);

        console.log(`📊 Extracted ${data.summary.total_companies} companies, ${data.summary.total_invoices} invoices, ${data.summary.total_items} items`);

        return data;
    } catch (error) {
        console.error('Error processing Bulgarian Gas Station PDF:', error);
        return null;
    }
}

/**
 * Convert extracted data to invoice format for API
 * @param {Object} extractedData - Data from extractBulgarianGasStationData
 * @param {Object} metadata - Upload metadata (company_id, type, invoiceRole, companyData, etc.)
 * @returns {Array} Array of invoice objects ready for API
 */
export function convertToInvoiceFormat(extractedData, metadata = {}) {
    const invoices = [];
    const invoiceRole = metadata.invoiceRole || 'buyer'; // 'buyer' or 'seller'
    const companyData = metadata.companyData || {};

    // Normalize company VAT number - remove BG prefix if present
    const companyVatNumber = companyData.vat_number
        ? companyData.vat_number.replace(/^BG/i, '').trim()
        : '';
    const companyTaxId = companyData.tax_id || companyVatNumber;

    console.log('🏢 Converting with company data:', {
        role: invoiceRole,
        companyName: companyData.name,
        companyVat: companyVatNumber,
        companyTaxId: companyTaxId
    });

    for (const company of extractedData.companies) {
        for (const invoice of company.invoices) {
            console.log(`🔄 Converting invoice ${invoice.invoice_number}:`, {
                company: company.company_name,
                eik: company.eik,
                date: invoice.date
            });

            // Create line items with tax rate
            const lineItems = invoice.items.map(item => ({
                description: item.product_name,
                quantity: item.quantity,
                unit_price: item.unit_price,
                tax_rate: '20%', // Bulgarian VAT rate
                total: item.total,
                barcode: item.barcode,
                internal_code: item.internal_code
            }));

            // Parse date (format: "1.12.2025 г." or "1.12.2025г." to "2025-12-01")
            // Remove any extra whitespace and the "г." suffix
            const cleanDate = invoice.date.trim().replace(/\s*г\.?\s*$/, '');
            console.log(`📅 Cleaning date: "${invoice.date}" → "${cleanDate}"`);

            let parsedDate = null;
            const dateMatch = cleanDate.match(/^(\d+)\.(\d+)\.(\d+)$/);
            if (dateMatch) {
                const [, day, month, year] = dateMatch;
                parsedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                console.log(`📅 Parsed date: ${invoice.date} → ${parsedDate}`);
            } else {
                console.warn(`⚠️ Failed to parse date: "${invoice.date}" (cleaned: "${cleanDate}")`);
                // Fallback to current date
                parsedDate = new Date().toISOString().split('T')[0];
                console.warn(`⚠️ Using fallback date: ${parsedDate}`);
            }

            // Determine buyer and seller based on invoice role
            let buyerData, sellerData;

            if (invoiceRole === 'buyer') {
                // We are buying from the gas station (expense)
                buyerData = {
                    buyer_name: companyData.name || null,
                    buyer_address: companyData.address || null,
                    buyer_tax_id: companyTaxId || null,
                    buyer_vat_number: companyVatNumber || null,
                    buyer_phone: companyData.phone || null,
                    buyer_email: companyData.email || null,
                    buyer_authorized_signatory: null
                };
                sellerData = {
                    seller_name: company.company_name,
                    seller_address: null,
                    seller_tax_id: company.eik,
                    seller_vat_number: company.eik,
                    seller_phone: null,
                    seller_email: null,
                    seller_authorized_signatory: null
                };
            } else {
                // We are selling to someone (income) - less common for gas stations
                buyerData = {
                    buyer_name: company.company_name,
                    buyer_address: null,
                    buyer_tax_id: company.eik,
                    buyer_vat_number: company.eik,
                    buyer_phone: null,
                    buyer_email: null,
                    buyer_authorized_signatory: null
                };
                sellerData = {
                    seller_name: companyData.name || null,
                    seller_address: companyData.address || null,
                    seller_tax_id: companyTaxId || null,
                    seller_vat_number: companyVatNumber || null,
                    seller_phone: companyData.phone || null,
                    seller_email: companyData.email || null,
                    seller_authorized_signatory: null
                };
            }

            // Create invoice object in the format expected by the API
            const invoiceData = {
                invoice_number: invoice.invoice_number,
                invoice_type: 'Invoice',
                invoice_date: parsedDate,
                issue_date: parsedDate,
                supply_date: parsedDate,
                due_date: parsedDate,
                currency: 'EUR', // Euro
                payment_method: metadata.paymentMethod || 'Bank Transfer',
                ...sellerData,
                ...buyerData,
                line_items: lineItems,
                subtotal: invoice.total_without_vat,
                tax_amount: invoice.vat,
                grand_total: invoice.total_with_vat,
                // Add metadata
                company_id: metadata.company_id || null,
                vendor_id: metadata.vendor_id || null,
                type: metadata.type || 'expense', // Gas station invoices are typically expenses
            };

            console.log(`✅ Converted invoice ${invoice.invoice_number}:`, {
                invoice_number: invoiceData.invoice_number,
                invoice_date: invoiceData.invoice_date,
                type: invoiceData.type,
                buyer_name: invoiceData.buyer_name,
                buyer_vat: invoiceData.buyer_vat_number,
                seller_name: invoiceData.seller_name,
                seller_vat: invoiceData.seller_vat_number,
                grand_total: invoiceData.grand_total
            });

            invoices.push(invoiceData);
        }
    }

    return invoices;
}

/**
 * Helper: Read file as ArrayBuffer
 */
function readFileAsArrayBuffer(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsArrayBuffer(file);
    });
}
